// ── BCHNostr — creator verification lookups ───────────────────────
//
// Author-filtered by design: a verification event is a signed claim by a
// creator about their OWN channel, so it is only ever read scoped to the
// video's author. A badge from author A can never vouch for author B.

import { useEffect, useMemo, useState } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  CREATOR_VERIFY_KIND, CREATOR_VERIFY_TAG,
  parseCreatorVerify, resolveVideoChannel, verifyPlatformForProvider,
  videoTipEligibility,
  type CreatorVerification, type VerifyPlatform,
  type VerificationLabel, type TipEligibility,
} from '../lib/creatorVerify';
import type { BCHVideo } from '../lib/video';

export type { VerificationLabel, TipEligibility };

/** All channels a pubkey has verified (newest per channel). */
export function useCreatorVerifications(pubkey: string | undefined): {
  verifications: CreatorVerification[];
  loading: boolean;
  refetch: () => void;
} {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [CREATOR_VERIFY_KIND], authors: [pubkey], '#t': [CREATOR_VERIFY_TAG], limit: 20 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const verifications = useMemo(() => {
    const byChannel = new Map<string, CreatorVerification>();
    for (const ev of events) {
      const v = parseCreatorVerify(ev);
      if (!v) continue;
      const existing = byChannel.get(v.channel);
      if (!existing || v.verifiedAt > existing.verifiedAt) byChannel.set(v.channel, v);
    }
    return [...byChannel.values()];
  }, [events]);

  return { verifications, loading, refetch };
}

/**
 * Auto-label a single video:
 *   'verified' — a video whose channel the poster has verified control of,
 *   'shared'   — a verifiable-platform video with no matching verification,
 *   'unknown'  — not applicable (non-verifiable provider, or channel not yet
 *                resolved).
 *
 * Verification currently supports YouTube and Odysee. The video→channel link
 * is resolved lazily and cached (see resolveVideoChannel), so this costs at
 * most one network call per distinct video and nothing on repeat views.
 */
export function useVideoVerification(video: BCHVideo | null | undefined): VerificationLabel {
  const platform: VerifyPlatform | null = video ? verifyPlatformForProvider(video.provider) : null;
  const { verifications } = useCreatorVerifications(platform ? video?.pubkey : undefined);
  const [channel, setChannel] = useState<string>('');

  // Only resolve the video's channel (a network call) when the creator
  // actually has verifications on THIS platform to match against. With none,
  // the answer is already 'shared' — so a feed of unverified videos costs
  // zero fetches.
  const platformVerifs = useMemo(
    () => verifications.filter(v => v.platform === platform),
    [verifications, platform],
  );
  const needChannel = !!platform && platformVerifs.length > 0;
  const videoId = needChannel ? (video?.playback?.id || '') : '';
  const provider = video?.provider || '';

  useEffect(() => {
    let cancelled = false;
    setChannel('');
    if (!videoId || !provider) return;
    resolveVideoChannel({ provider, playback: { id: videoId } })
      .then(res => { if (!cancelled) setChannel(res?.channel || ''); });
    return () => { cancelled = true; };
  }, [videoId, provider]);

  return useMemo<VerificationLabel>(() => {
    if (!platform) return 'unknown';
    // No verifications on this platform → it's a shared video (soft label).
    if (platformVerifs.length === 0) return 'shared';
    // Channel not resolved yet → don't guess.
    if (!channel) return 'unknown';
    // resolveVideoChannel returns an already-normalized channel key, so a
    // direct comparison against the (also-normalized) stored channel is exact.
    const match = platformVerifs.some(v => v.channel === channel);
    return match ? 'verified' : 'shared';
  }, [platform, platformVerifs, channel]);
}

/**
 * Whether a video may be tipped, and its underlying verification label.
 *
 * This is the hook every tip surface should consult before showing a tip
 * control on a video. It layers the central `videoTipEligibility` policy on
 * top of the auto-derived label:
 *
 *   · YouTube / Odysee → 'eligible' only when the poster proved they created
 *     it; 'unverified' otherwise (tip disabled); 'checking' while resolving.
 *   · Nostr-native uploads and every other provider → 'eligible'.
 */
export function useVideoTipEligibility(video: BCHVideo | null | undefined): {
  eligibility: TipEligibility;
  label: VerificationLabel;
} {
  const label = useVideoVerification(video);
  const eligibility = useMemo<TipEligibility>(
    () => (video ? videoTipEligibility(video.provider, label) : 'eligible'),
    [video, label],
  );
  return { eligibility, label };
}
